import { useCallback, useRef } from "react";
import { Button, ColorPicker, InputNumber, Space, Typography } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
} from "@ant-design/icons";
import FontPicker from "./FontPicker";
import { getInspectableTextObject } from "./invitationFabricUtils";

export default function Toolbar({ activeObject, onUpdateObject }) {
  const textObj = getInspectableTextObject(activeObject);

  /**
   * Keep a ref to the text object so the `apply` callback stays stable
   * even when the Toolbar re-renders between an async font load and its
   * completion callback.  Without this the stale closure inside FontPicker
   * could target a detached / null object.
   */
  const textObjRef = useRef(textObj);
  textObjRef.current = textObj;

  const apply = useCallback(
    (patch) => {
      const obj = textObjRef.current;
      if (!obj) return;
      obj.set(patch);
      obj.canvas?.requestRenderAll();
      onUpdateObject?.(obj);
    },
    [onUpdateObject]
  );

  if (!textObj) {
    return null;
  }

  const fontFamily = textObj.fontFamily || "Inter";
  const fontSize = textObj.fontSize || 24;
  const fontWeight = textObj.fontWeight || "normal";
  const fontStyle = textObj.fontStyle || "normal";
  const fill =
    typeof textObj.fill === "string" ? textObj.fill : "#111827";
  const textAlign = textObj.textAlign || "left";

  return (
    <div className="invitation-builder__toolbar">
      <Space wrap size="middle" align="center">
        <FontPicker
          value={fontFamily}
          onChange={(f) => apply({ fontFamily: f })}
          style={{ minWidth: 200 }}
        />
        <Space size={6} align="center" className="invitation-builder__toolbar-size-field">
          <Typography.Text type="secondary" className="invitation-builder__toolbar-size-label">
            Size
          </Typography.Text>
          <InputNumber
            min={8}
            max={200}
            value={fontSize}
            onChange={(v) => v != null && apply({ fontSize: v })}
            controls={false}
            className="invitation-builder__toolbar-font-size"
          />
        </Space>
        <Button
          type={
            fontWeight === "bold" || fontWeight === 700 ? "primary" : "default"
          }
          icon={<BoldOutlined />}
          onClick={() =>
            apply({
              fontWeight:
                fontWeight === "bold" || fontWeight === 700
                  ? "normal"
                  : "bold",
            })
          }
        />
        <Button
          type={fontStyle === "italic" ? "primary" : "default"}
          icon={<ItalicOutlined />}
          onClick={() =>
            apply({
              fontStyle: fontStyle === "italic" ? "normal" : "italic",
            })
          }
        />
        <ColorPicker
          value={fill}
          onChange={(c) =>
            apply({
              fill:
                typeof c?.toHexString === "function" ? c.toHexString() : fill,
            })
          }
          showText
        />
        <Button
          type={textAlign === "left" ? "primary" : "default"}
          icon={<AlignLeftOutlined />}
          onClick={() => apply({ textAlign: "left" })}
        />
        <Button
          type={textAlign === "center" ? "primary" : "default"}
          icon={<AlignCenterOutlined />}
          onClick={() => apply({ textAlign: "center" })}
        />
        <Button
          type={textAlign === "right" ? "primary" : "default"}
          icon={<AlignRightOutlined />}
          onClick={() => apply({ textAlign: "right" })}
        />
      </Space>
    </div>
  );
}
